import * as React from 'react';
import {
  BrowserRouter as RRD_BrowserRouter,
  Link as RRD_Link,
  Navigate as RRD_Navigate,
  Outlet as RRD_Outlet,
  Route as RRD_Route,
  Routes as RRD_Routes,
  useLocation as rrdUseLocation,
  useNavigate as rrdUseNavigate,
  useParams as rrdUseParams,
} from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';
import { runNavigationGuards } from './navigation-guard';

export type { LinkProps, Location, NavigateFunction, NavigateProps } from 'react-router-dom';

/**
 * Sole module that imports `react-router-dom`.
 *
 * `BrowserRouter`, `Routes`, `Route`, `Outlet`, and `Navigate` must be the **same
 * component references** as react-router-dom exports. React Router walks `<Routes>`
 * children and asserts each is a real `<Route>`; a wrapper function breaks that.
 *
 * `AppLink` and the hooks stay as thin wrappers / call-throughs.
 */
export const AppBrowserRouter = RRD_BrowserRouter;
export const AppRoutes = RRD_Routes;
export const AppRoute = RRD_Route;
export const AppOutlet = RRD_Outlet;
export const AppNavigate = RRD_Navigate;

export const AppLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<typeof RRD_Link>
>(function AppLink({ onClick, to, ...props }, ref) {
  return (
    <RRD_Link
      ref={ref}
      to={to}
      onClick={event => {
        // `to` may be a `To` object; String() on one yields "[object Object]".
        const path =
          typeof to === 'string'
            ? to
            : ((to as { pathname?: string })?.pathname ?? '');

        if (!runNavigationGuards(path)) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...props}
    />
  );
});

export function useAppLocation() {
  return rrdUseLocation();
}

export function useAppNavigate() {
  const navigate = rrdUseNavigate();

  return React.useMemo(() => {
    const guarded = ((to: unknown, options?: unknown) => {
      // A numeric argument is a history delta (navigate(-1)), which has no
      // destination path to hand a guard.
      if (typeof to === 'number') return navigate(to);

      // react-router also accepts a `To` object; String() on one yields
      // "[object Object]", which would reach guards as a meaningless path.
      const path =
        typeof to === 'string'
          ? to
          : ((to as { pathname?: string })?.pathname ?? '');

      if (!runNavigationGuards(path)) return;

      return navigate(to as string, options as never);
    }) as NavigateFunction;

    return guarded;
  }, [navigate]);
}

export function useAppParams<
  T extends Record<string, string | undefined> | string = string,
>() {
  return rrdUseParams<T>();
}

/** Same component as `AppNavigate` — kept for existing call sites. */
export const NavigateTo = RRD_Navigate;
